import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useResponsive } from '../../../../hooks/useResponsive';
import { supabaseApi } from '../../../../lib/api/supabase';
import { SupabaseConfigResponse } from '@optidev/shared-types';

import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { Label } from '../../../../components/ui/label';
import { cn } from '../../../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';

import {
  Code,
  Upload,
  Trash2,
  ExternalLink,
  Copy,
  Play,
  X,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface EdgeFunctionsPanelProps {
  projectId: string;
}

export function EdgeFunctionsPanel({ projectId }: EdgeFunctionsPanelProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<any>(null);
  const [config, setConfig] = useState<SupabaseConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [creatingSample, setCreatingSample] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testFunctionName, setTestFunctionName] = useState<string>('');
  const [testMethod, setTestMethod] = useState('POST');
  const [testBody, setTestBody] = useState('{ "name": "Functions" }');
  const [testHeaders, setTestHeaders] = useState<Array<{ key: string; value: string }>>([]);
  const [testParams, setTestParams] = useState<Array<{ key: string; value: string }>>([{ key: 'name', value: 'Functions' }]);
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [functionToDelete, setFunctionToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);


  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [projectId, token]);

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [functionStatus, projectConfig] = await Promise.all([
        supabaseApi.getFunctionStatus(token, projectId),
        supabaseApi.getProjectConfig(token, projectId),
      ]);
      setStatus(functionStatus);
      setConfig(projectConfig);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load functions: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeploy = async (functionName: string) => {
    if (!token) return;
    setDeploying(functionName);
    try {
      await supabaseApi.deployFromWorkspace(token, projectId, functionName);
      toast.success(`Function ${functionName} deployed successfully`);
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Deployment failed: ${errorMessage}`);
    } finally {
      setDeploying(null);
    }
  };

  const confirmDelete = (functionId: string, functionName: string) => {
    setFunctionToDelete({ id: functionId, name: functionName });
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!token || !functionToDelete) return;

    setDeleting(true);
    try {
      await supabaseApi.deleteFunction(token, projectId, functionToDelete.id);
      toast.success(`Function ${functionToDelete.name} deleted`);
      setDeleteModalOpen(false);
      setFunctionToDelete(null);
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to delete: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const copyFunctionUrl = (functionName: string) => {
    if (!config?.apiUrl) return;
    const functionUrl = `${config.apiUrl.replace('/rest/v1', '')}/functions/v1/${functionName}`;
    navigator.clipboard.writeText(functionUrl);
    toast.success('Function URL copied to clipboard');
  };

  const handleCreateSample = async () => {
    if (!token) return;
    setCreatingSample(true);
    try {
      await supabaseApi.createSampleFunction(token, projectId);
      toast.success('Sample function created in workspace');
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to create sample: ${errorMessage}`);
    } finally {
      setCreatingSample(false);
    }
  };

  const openTestModal = (functionName: string) => {
    setTestFunctionName(functionName);
    setTestModalOpen(true);
    setTestResponse(null);
  };

  const handleTest = async () => {
    if (!token) return;
    setTesting(true);
    setTestResponse(null);

    try {
      // Parse body for non-GET requests (only if body is provided)
      let body: unknown = undefined;
      if (testMethod !== 'GET' && testBody.trim()) {
        try {
          body = JSON.parse(testBody);
        } catch {
          toast.error('Invalid JSON body');
          setTesting(false);
          return;
        }
      }

      // Call backend proxy endpoint (signs gateway JWT and calls Supabase directly)
      const response = await supabaseApi.testFunction(token, projectId, testFunctionName, {
        method: testMethod,
        body,
        headers: testHeaders.filter(h => h.key && h.value),
        params: testParams.filter(p => p.key && p.value),
      });

      setTestResponse({
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        duration: response.duration,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setTestResponse({
        status: 0,
        statusText: 'Error',
        data: { error: errorMessage },
        duration: 0,
        error: true,
      });
    } finally {
      setTesting(false);
    }
  };

  // Navigate to Logs tab with function pre-selected
  const handleViewLogs = (functionSlug: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('cloudTab', 'logs');
    newParams.set('functionSlug', functionSlug);
    setSearchParams(newParams, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
          <span>Loading functions...</span>
        </div>
      </div>
    );
  }

  const workspaceFunctions = status?.workspace || [];
  const deployedFunctions = status?.deployed || [];
  return (
    <div className={cn("space-y-4", isMobile && "space-y-3")}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={cn(isMobile ? "text-base" : "text-lg", "font-semibold")}>Functions</h3>
          {!isMobile && (
            <p className="text-sm text-muted-foreground mt-0.5">
              Deploy and manage serverless edge functions
            </p>
          )}
        </div>
        <Button onClick={loadData} variant="ghost" size="sm">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Workspace Functions */}
      {workspaceFunctions.length > 0 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium">Workspace</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Found in <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">supabase/functions/</code>
            </p>
          </div>
          <div className="space-y-2">
            {workspaceFunctions.map((func: any) => {
              const isDeployed = deployedFunctions.some(
                (d: any) => d.name === func.name || d.slug === func.name
              );
              const isDeploying = deploying === func.name;

              return (
                <div key={func.name} className={cn(
                  "rounded-lg border border-border/60 p-4 hover:border-border transition-colors",
                  isMobile ? "flex flex-col gap-3" : "flex items-center justify-between"
                )}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${isDeployed ? 'bg-emerald-500/10' : 'bg-muted'}`}>
                      <Code className={`w-4 h-4 ${isDeployed ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{func.name}</span>
                        {isDeployed ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/10 text-emerald-600">
                            Live
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-muted text-muted-foreground">
                            Not deployed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">{func.path}</div>
                    </div>
                  </div>
                  <div className={cn("flex items-center gap-2", isMobile && "justify-end")}>
                    {isDeployed && (
                      <Button
                        onClick={() => openTestModal(func.name)}
                        variant="ghost"
                        size="sm"
                      >
                        <Play className="w-3.5 h-3.5" />
                        {isMobile && <span className="ml-1">Test</span>}
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDeploy(func.name)}
                      disabled={isDeploying}
                      size="sm"
                      variant={isDeployed ? "outline" : "default"}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      {isDeploying ? 'Deploying...' : isDeployed ? 'Redeploy' : 'Deploy'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Deployed Functions */}
      {deployedFunctions.length > 0 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium">Deployed</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Running on OptiDev Cloud
            </p>
          </div>
          <div className="space-y-2">
            {deployedFunctions.map((func: any) => (
              <div key={func.id} className="rounded-lg border border-border/60 p-4 hover:border-border transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                      <Code className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{func.name || func.slug}</div>
                      <div className="text-xs text-muted-foreground">
                        v{func.version || 1} · {new Date(func.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => handleViewLogs(func.slug || func.name)}
                      variant="ghost"
                      size="sm"
                      title="View logs"
                    >
                      <span className="text-xs">Logs</span>
                    </Button>
                    <Button
                      onClick={() => copyFunctionUrl(func.name || func.slug)}
                      variant="ghost"
                      size="sm"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      onClick={() => confirmDelete(func.slug, func.name || func.slug)}
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Function URL */}
                {config?.apiUrl && (
                  <div className="mt-3 pt-3 border-t border-border/40">
                    <code className="text-xs text-muted-foreground font-mono bg-muted/50 px-3 py-2 rounded-md block truncate">
                      {config.apiUrl.replace('/rest/v1', '')}/functions/v1/{func.name || func.slug}
                    </code>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {workspaceFunctions.length === 0 && deployedFunctions.length === 0 && (
        <div className="rounded-lg border border-dashed border-border/60 p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="p-4 rounded-full bg-muted/50 mb-4">
              <Code className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold mb-1">No functions yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Create serverless functions in your workspace to deploy them to the edge.
            </p>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleCreateSample}
                disabled={creatingSample}
                size="sm"
              >
                <Code className="w-3.5 h-3.5 mr-1.5" />
                {creatingSample ? 'Creating...' : 'Create Sample'}
              </Button>
              <a
                href="https://supabase.com/docs/guides/functions"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Learn more
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Function</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete this function? This action cannot be undone.
            </p>
            {functionToDelete && (
              <div className="bg-muted/30 border border-border/40 p-3.5 rounded-lg">
                <p className="text-sm font-medium font-mono">{functionToDelete.name}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setFunctionToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Function'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
        <DialogContent className={cn(
          "flex flex-col gap-0 p-0",
          isMobile ? "max-w-[95vw] max-h-[90vh]" : "max-w-2xl max-h-[85vh]"
        )}>
          <DialogHeader className={cn("border-b border-border", isMobile ? "px-4 py-3" : "px-6 py-4")}>
            <DialogTitle className="text-base font-semibold">Test Function</DialogTitle>
          </DialogHeader>

          <div className={cn("flex-1 overflow-y-auto space-y-4", isMobile ? "px-4 py-4" : "px-6 py-5 space-y-5")}>
            {/* Function & Method Row */}
            <div className={cn(isMobile ? "flex flex-col gap-2" : "flex items-center gap-3")}>
              <div className={cn("flex items-center gap-2", isMobile && "w-full")}>
                <Select value={testMethod} onValueChange={setTestMethod}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="DELETE">DELETE</SelectItem>
                    <SelectItem value="PATCH">PATCH</SelectItem>
                  </SelectContent>
                </Select>
                {!isMobile && (
                  <Button
                    onClick={handleTest}
                    disabled={testing}
                    size="sm"
                  >
                    {testing ? 'Sending...' : 'Send'}
                  </Button>
                )}
              </div>
              <div className={cn(
                "px-3 py-2 bg-muted/50 rounded-md font-mono text-sm text-muted-foreground",
                isMobile ? "text-xs break-all" : "flex-1 truncate"
              )}>
                /functions/v1/<span className="text-foreground">{testFunctionName}</span>
              </div>
              {isMobile && (
                <Button
                  onClick={handleTest}
                  disabled={testing}
                  size="sm"
                  className="w-full"
                >
                  {testing ? 'Sending...' : 'Send Request'}
                </Button>
              )}
            </div>

            {/* Request Body */}
            {testMethod !== 'GET' && (
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Body</Label>
                <Textarea
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                  className="font-mono text-sm min-h-[120px] bg-muted/30"
                  placeholder='{ "key": "value" }'
                />
              </div>
            )}

            {/* Headers */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Headers</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTestHeaders([...testHeaders, { key: '', value: '' }])}
                  className="h-7 text-xs"
                >
                  Add
                </Button>
              </div>
              {testHeaders.length > 0 ? (
                <div className="space-y-2">
                  {testHeaders.map((header, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={header.key}
                        onChange={(e) => {
                          const newHeaders = [...testHeaders];
                          newHeaders[index].key = e.target.value;
                          setTestHeaders(newHeaders);
                        }}
                        className="flex-1 h-9 text-sm bg-muted/30"
                        placeholder="Key"
                      />
                      <Input
                        value={header.value}
                        onChange={(e) => {
                          const newHeaders = [...testHeaders];
                          newHeaders[index].value = e.target.value;
                          setTestHeaders(newHeaders);
                        }}
                        className="flex-1 h-9 text-sm bg-muted/30"
                        placeholder="Value"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTestHeaders(testHeaders.filter((_, i) => i !== index))}
                        className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No headers added</p>
              )}
            </div>

            {/* Query Parameters */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Query Parameters</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTestParams([...testParams, { key: '', value: '' }])}
                  className="h-7 text-xs"
                >
                  Add
                </Button>
              </div>
              {testParams.length > 0 ? (
                <div className="space-y-2">
                  {testParams.map((param, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={param.key}
                        onChange={(e) => {
                          const newParams = [...testParams];
                          newParams[index].key = e.target.value;
                          setTestParams(newParams);
                        }}
                        className="flex-1 h-9 text-sm bg-muted/30"
                        placeholder="Key"
                      />
                      <Input
                        value={param.value}
                        onChange={(e) => {
                          const newParams = [...testParams];
                          newParams[index].value = e.target.value;
                          setTestParams(newParams);
                        }}
                        className="flex-1 h-9 text-sm bg-muted/30"
                        placeholder="Value"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTestParams(testParams.filter((_, i) => i !== index))}
                        className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No parameters added</p>
              )}
            </div>

            {/* Response */}
            {testResponse && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Response</Label>
                  <div className="flex items-center gap-2">
                    {testResponse.duration > 0 && (
                      <span className="text-xs text-muted-foreground">{testResponse.duration}ms</span>
                    )}
                    <span className={`text-xs font-mono px-2 py-1 rounded-md ${
                      testResponse.error || testResponse.status >= 400
                        ? 'bg-red-500/10 text-red-500'
                        : 'bg-emerald-500/10 text-emerald-500'
                    }`}>
                      {testResponse.status} {testResponse.statusText}
                    </span>
                  </div>
                </div>
                <div className="bg-zinc-950 rounded-lg p-4 overflow-x-auto border border-border/40">
                  <pre className="text-sm font-mono text-zinc-300 whitespace-pre-wrap">
                    {JSON.stringify(testResponse.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-border flex justify-end">
            <Button
              onClick={() => setTestModalOpen(false)}
              variant="outline"
              size="sm"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
